import "../styles/instructions.css";

const GENERATION_STEPS = [
  {
    title: "Set up a campaign",
    description:
      "Choose a unique designator, add a recognizable campaign name, select the active dates, and enter the number of codes needed.",
  },
  {
    title: "Review before submitting",
    description:
      "Confirm the campaign information and quantity. Once submitted, code preparation continues safely in the background.",
  },
  {
    title: "Follow preparation",
    description:
      "Use the campaign detail view to see how many codes are ready and whether the campaign needs attention. You can leave the page while preparation continues.",
  },
  {
    title: "Use the available inventory",
    description:
      "Prepared codes are held in the secure Cologuard inventory until an approved partner request reserves one for use.",
  },
];

const PARTNER_STEPS = [
  {
    title: "Request by designator",
    description:
      "The approved partner experience requests a code using the campaign designator and credentials provided by Stran.",
  },
  {
    title: "Receive one available code",
    description:
      "Cologuard selects an available code from that campaign, reserves it, and returns it through the approved delivery process.",
  },
  {
    title: "Track its activity",
    description:
      "As the code moves through the customer experience, its requested, order, and completion information becomes visible in History.",
  },
];

const ROLE_GUIDANCE = {
  viewer: "You can review the Summary, History, campaign details, and these instructions.",
  manager:
    "You can create campaigns, follow their progress, retry a campaign that needs attention, and review all reporting views.",
  admin:
    "You can use every campaign and reporting feature and invite users through User Management.",
};

function StepList({ steps }) {
  return (
    <ol className="cg-instructions-steps">
      {steps.map((step, index) => (
        <li key={step.title}>
          <span className="cg-instructions-step-number" aria-hidden="true">
            {index + 1}
          </span>
          <div>
            <h3>{step.title}</h3>
            <p>{step.description}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function InstructionsPage({ currentUser }) {
  const role = String(currentUser?.role || "viewer").toLowerCase();

  return (
    <main className="cg-page-shell cg-instructions-page">
      <section className="cg-instructions-hero" aria-labelledby="instructions-title">
        <div>
          <span className="cg-kicker">Instructions</span>
          <h2 id="instructions-title">From campaign setup to code use</h2>
          <p>
            Cologuard provides one place to prepare campaign codes, monitor their readiness,
            and understand how they are being used.
          </p>
        </div>
        <div className="cg-instructions-role-card">
          <span>Your access</span>
          <strong>{role}</strong>
          <p>{ROLE_GUIDANCE[role] || ROLE_GUIDANCE.viewer}</p>
        </div>
      </section>

      <section className="cg-panel cg-instructions-section" aria-labelledby="prepare-codes-title">
        <div className="cg-instructions-section-heading">
          <span className="cg-instructions-part">Part 1</span>
          <div>
            <h2 id="prepare-codes-title">Prepare campaign codes</h2>
            <p>
              Managers and administrators create campaigns. Every authenticated user can
              follow campaign readiness and review previous activity.
            </p>
          </div>
        </div>
        <StepList steps={GENERATION_STEPS} />
      </section>

      <section className="cg-panel cg-instructions-section" aria-labelledby="partner-use-title">
        <div className="cg-instructions-section-heading">
          <span className="cg-instructions-part cg-instructions-part-secondary">Part 2</span>
          <div>
            <h2 id="partner-use-title">Provide codes to the partner experience</h2>
            <p>
              Once a campaign has available codes, the approved partner connection can request
              them. Partner credentials are managed by Stran and are not entered in this app.
            </p>
          </div>
        </div>
        <StepList steps={PARTNER_STEPS} />
      </section>

      <section className="cg-instructions-reference" aria-labelledby="views-title">
        <div className="cg-instructions-reference-copy">
          <span className="cg-kicker">Using the workspace</span>
          <h2 id="views-title">Where to find information</h2>
          <p>Each view has a focused purpose, so you can quickly choose where to go.</p>
        </div>
        <div className="cg-instructions-view-grid">
          <article>
            <h3>Summary</h3>
            <p>See current code availability, recent activity, and the latest campaigns.</p>
          </article>
          <article>
            <h3>Campaign Setup</h3>
            <p>Create and review a campaign before requesting its codes.</p>
          </article>
          <article>
            <h3>History</h3>
            <p>Search campaigns, check code totals, and open a campaign’s full details.</p>
          </article>
          <article>
            <h3>User Management</h3>
            <p>Administrators can invite users and assign the appropriate access level.</p>
          </article>
        </div>
      </section>

      <aside className="cg-instructions-support" aria-labelledby="support-title">
        <div className="cg-instructions-support-mark" aria-hidden="true">?</div>
        <div>
          <h2 id="support-title">If something does not look right</h2>
          <p>
            Try refreshing the relevant view first. If the issue continues, contact the Stran
            team with the campaign designator, approximate time, action attempted, and message
            shown on screen. Do not include passwords or partner credentials.
          </p>
        </div>
      </aside>
    </main>
  );
}
